/**
 * The source of clips: tap to record, tap to stop, the clip goes to `onClip`.
 *
 * This hook owns the microphone and nothing else. It does not know what a transcript
 * is — `onClip(blob, durationMs)` returns a promise and the button stays "busy" until
 * it settles — so the VAD phase (segments instead of whole clips) replaces this file
 * and leaves the upload and the insertion untouched. That split is the work order's
 * one design requirement (frontend-mic-button.md §6).
 *
 * The rules that are not decoration, each one a failure this surface actually has:
 *  - getUserMedia is called INSIDE the tap handler. iOS grants the mic only from a
 *    user gesture; move the call past an await that is not the permission prompt
 *    itself and the tap stops counting.
 *  - The cap (120 s) is the service's limit, so it is enforced here: a longer clip is
 *    a guaranteed 413 after the upload it wasted.
 *  - Backgrounding and phone calls end the stream. The work order says that is "stop
 *    and upload what we have", not an error — a person who took a call mid-sentence
 *    still wants the sentence.
 *  - Every track is stopped when recording ends, whatever ended it, or the browser's
 *    recording indicator stays lit with no way to turn it off.
 *  - The clock (setInterval) asks the browser what time it is, never a machine; it
 *    lives exactly as long as a recording and is cleared with it.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * `stopping` is the gap between asking the recorder to stop and the browser saying it
 * has — see `stop()`. It exists because that gap is visible to a person and was being
 * rendered as "still recording".
 */
export type RecorderState = 'idle' | 'starting' | 'recording' | 'stopping' | 'uploading'

/** The service refuses longer clips (413 too_long); stop before it has to. */
export const MAX_RECORD_MS = 120_000

const CLOCK_MS = 250

/** Why recording cannot start here, in words for the person, or null. */
function unsupportedReason(): string | null {
  if (typeof window === 'undefined') return 'Voice input is not available here.'
  if (!window.isSecureContext) return 'Voice input needs HTTPS. Open the https:// address.'
  if (!navigator.mediaDevices?.getUserMedia) return 'This browser cannot access the microphone.'
  if (typeof window.MediaRecorder === 'undefined') return 'This browser cannot record audio.'
  return null
}

/** The DOMException names getUserMedia actually throws, each as the next thing to do. */
function permissionProblem(e: unknown): string {
  const name = (e as { name?: string })?.name ?? ''
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Microphone access was blocked. Allow it for this site and try again.'
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'No microphone was found on this device.'
    case 'NotReadableError':
      return 'The microphone is busy in another app. Close it and try again.'
    default:
      return 'Could not start the microphone.'
  }
}

/** The first container this browser will actually record; '' lets it choose. */
function pickMime(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
  for (const type of candidates) {
    try {
      if (window.MediaRecorder.isTypeSupported(type)) return type
    } catch {
      // isTypeSupported throws on some engines; treat as unsupported.
    }
  }
  return ''
}

export function useVoiceRecorder({
  onClip,
  onProblem,
  maxMs,
}: {
  /** The finished recording. The hook stays `uploading` until this settles. */
  onClip: (blob: Blob, durationMs: number) => Promise<void>
  /** A sentence for the person: permission refused, nothing captured, and so on. */
  onProblem: (message: string) => void
  /**
   * How long THIS recording may run, asked at each start and clamped to the
   * service's cap — the billing gate answers min(120 s, what is left today), because
   * a clip that would cross the day's limit is refused whole (billing/voiceGate.ts).
   */
  maxMs?: () => number
}) {
  const [state, setState] = useState<RecorderState>('idle')
  const [elapsedMs, setElapsedMs] = useState(0)
  const recorder = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const chunks = useRef<Blob[]>([])
  const startedAt = useRef(0)
  const clock = useRef<number | null>(null)
  const cap = useRef<number | null>(null)
  // Read through refs so the recorder's handlers, bound once, never see stale props.
  const clipRef = useRef(onClip)
  const problemRef = useRef(onProblem)
  const maxRef = useRef(maxMs)
  clipRef.current = onClip
  problemRef.current = onProblem
  maxRef.current = maxMs

  const clearTimers = useCallback(() => {
    if (clock.current !== null) {
      window.clearInterval(clock.current)
      clock.current = null
    }
    if (cap.current !== null) {
      window.clearTimeout(cap.current)
      cap.current = null
    }
  }, [])

  const releaseMic = useCallback(() => {
    for (const track of stream.current?.getTracks() ?? []) {
      try {
        track.stop()
      } catch {
        // already ended
      }
    }
    stream.current = null
  }, [])

  const finish = useCallback(async () => {
    clearTimers()
    const durationMs = startedAt.current ? Date.now() - startedAt.current : 0
    const type = recorder.current?.mimeType || chunks.current[0]?.type || 'audio/webm'
    const blob = new Blob(chunks.current, { type })
    chunks.current = []
    recorder.current = null
    releaseMic()
    if (blob.size === 0) {
      setState('idle')
      problemRef.current('No audio was captured.')
      return
    }
    setState('uploading')
    try {
      await clipRef.current(blob, durationMs)
    } finally {
      setState('idle')
      setElapsedMs(0)
    }
  }, [clearTimers, releaseMic])

  /**
   * ACKNOWLEDGE THE PRESS BEFORE THE BROWSER DOES — owner, 2026-09-15: 麥克風的按鈕
   * 有時候不太靈敏, 想要結束錄音的時候按下去, 它感覺很不容易停止, 要按個兩三次它才會生效.
   *
   * `MediaRecorder.stop()` sets `state` to "inactive" SYNCHRONOUSLY and then QUEUES
   * the dataavailable and stop events. So there is a real gap — longer on a phone,
   * and longer still because the clock below re-renders the app four times a second
   * while recording — in which the recorder is already stopped and this hook still
   * said `recording`. The button kept its clock ticking, nothing on screen had
   * changed, and the person pressed again.
   *
   * THE SECOND PRESS DID NOTHING, SILENTLY: it arrived here, found
   * `rec.state === 'inactive'`, and returned. So did the third. It appeared to "work
   * on the third try" only because onstop happened to land around then — the press
   * that seemed to succeed had no more effect than the two before it.
   *
   * The fix is not to make stopping faster; it is to stop lying about it. The clock
   * is cleared and the state moves the moment the press is accepted, so the button
   * dims at once and a second press is refused by a control that visibly says it is
   * already busy rather than by a guard nobody can see.
   *
   * Why the timers are cleared HERE and not only in finish(): finish() runs on
   * onstop, which is precisely the event being waited for. Leaving the clock to it is
   * what kept the seconds counting under a button that had already been told to stop.
   */
  const stop = useCallback(() => {
    const rec = recorder.current
    if (!rec || rec.state === 'inactive') return
    clearTimers()
    setState('stopping')
    rec.stop() // onstop → finish
  }, [clearTimers])

  const start = useCallback(async () => {
    const why = unsupportedReason()
    if (why) {
      problemRef.current(why)
      return
    }
    setState('starting')
    let got: MediaStream
    try {
      // Synchronously inside the tap's call chain — see the file's head.
      got = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      setState('idle')
      problemRef.current(permissionProblem(e))
      return
    }
    stream.current = got
    const mime = pickMime()
    let rec: MediaRecorder
    try {
      rec = new MediaRecorder(got, mime ? { mimeType: mime } : undefined)
    } catch {
      try {
        rec = new MediaRecorder(got) // some engines refuse every explicit type
      } catch {
        releaseMic()
        setState('idle')
        problemRef.current('This browser cannot record audio.')
        return
      }
    }
    chunks.current = []
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.current.push(e.data)
    }
    rec.onstop = () => void finish()
    // A track that ENDS on its own (a phone call took the mic, the OS revoked it) is
    // the "stop and upload what we have" case, not an error.
    for (const track of got.getAudioTracks()) {
      track.addEventListener('ended', () => stop())
    }
    recorder.current = rec
    rec.start()
    startedAt.current = Date.now()
    setElapsedMs(0)
    setState('recording')
    let second = 0
    clock.current = window.setInterval(() => {
      // Every reader shows whole seconds: the clock moves when the second does, not every tick — each move renders the
      // app around the button (owner, 2026-11-04: UI 有時候會變得很卡). Not even a same-value set: React may still render
      // once to find that out.
      const now = Date.now() - startedAt.current
      if (Math.floor(now / 1000) === second) return
      second = Math.floor(now / 1000)
      setElapsedMs(now)
    }, CLOCK_MS)
    const asked = maxRef.current?.()
    const limit = typeof asked === 'number' && asked > 0 ? Math.min(asked, MAX_RECORD_MS) : MAX_RECORD_MS
    cap.current = window.setTimeout(() => stop(), limit)
  }, [finish, releaseMic, stop])

  /** The button's one gesture: idle → record, recording → stop and upload. */
  const toggle = useCallback(() => {
    if (state === 'recording') stop()
    else if (state === 'idle') void start()
    // starting / stopping / uploading: a second tap does nothing rather than
    // double-start — and now the button is visibly disabled in all three, so the
    // refusal is something the person can see rather than something they discover.
  }, [state, start, stop])

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState !== 'visible') stop()
    }
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', stop)
    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', stop)
      // Unmount must not leave the mic open.
      clearTimers()
      const rec = recorder.current
      recorder.current = null
      if (rec && rec.state !== 'inactive') {
        rec.onstop = null
        try {
          rec.stop()
        } catch {
          // already stopped
        }
      }
      releaseMic()
    }
  }, [stop, clearTimers, releaseMic])

  return { state, elapsedMs, toggle, supported: unsupportedReason() === null }
}
